/**
 * League of Managers - Simulation & Domain Logic Engine
 * Asistencia, Economía, Prensa Deportiva, Match Engine y Mercado
 */

import { Club, Player, MatchEngineCalculation, Stadium, ClubStaff, Fixture } from '../models/types';

/**
 * Calcular asistencia
 * - Considera abonados (mín 50% asisten siempre, incluso con mala racha)
 * - Precio de entradas vs precio de referencia (35€)
 * - Dinámica reciente del equipo (+asistencia con más victorias)
 * - Capacidad máxima
 */
export function calculateAttendance(
  stadium: Stadium,
  recentForm: ('W' | 'D' | 'L')[]
): {
  attendance: number;
  percentage: number;
  generalTicketsSold: number;
  seasonTicketsAttending: number;
} {
  const { capacity, seasonTicketHolders, ticketPrice } = stadium;

  // Form factor: each win adds 4%, each draw adds 2%, each loss subtracts 3%
  let formModifier = 0;
  recentForm.forEach((res) => {
    if (res === 'W') formModifier += 0.04;
    else if (res === 'D') formModifier += 0.01;
    else formModifier -= 0.03;
  });

  // Season ticket holders attendance: base 70%, modulated by form (min 50% guaranteed)
  const seasonTicketAttendanceRate = Math.max(0.5, Math.min(0.98, 0.72 + formModifier));
  const seasonTicketsAttending = Math.round(seasonTicketHolders * seasonTicketAttendanceRate);

  // Available seats for general public
  const generalCapacity = Math.max(0, capacity - seasonTicketHolders);

  // General tickets demand based on price elasticity (reference: 35€)
  // Higher price lowers demand, lower price increases demand
  const priceElasticity = Math.max(0.2, 1 - (ticketPrice - 35) * 0.025);
  const generalDemandRate = Math.max(0.15, Math.min(0.95, (0.55 + formModifier) * priceElasticity));
  const generalTicketsSold = Math.round(generalCapacity * generalDemandRate);

  const totalAttendance = Math.min(capacity, seasonTicketsAttending + generalTicketsSold);
  const percentage = Math.round((totalAttendance / capacity) * 100);

  return {
    attendance: totalAttendance,
    percentage,
    generalTicketsSold,
    seasonTicketsAttending,
  };
}

/**
 * Registrar ingresos de partido
 * - Entradas taquilla ordinaria
 * - Cafetería (módulo nivel 1 a 5)
 * - Parking (módulo nivel 1 a 5)
 * - Merchandising (módulo nivel 1 a 5)
 */
export function calculateMatchdayRevenues(
  generalTicketsSold: number,
  ticketPrice: number,
  attendance: number,
  stadium: Stadium
): {
  ticketRevenueM: number;
  commercialRevenueM: number;
  breakdown: { cafeteriaM: number; parkingM: number; merchandisingM: number };
} {
  // Ticket revenue in M€
  const ticketRevenueM = Number(((generalTicketsSold * ticketPrice) / 1_000_000).toFixed(3));

  // Commercial modules revenue based on level and attendance
  const { parkingLevel, cafeteriaLevel, merchandisingLevel } = stadium.modules;

  // Average spend per spectator modulated by facility tier
  const cafeteriaPerCapita = 1.2 + cafeteriaLevel * 0.8; // e.g. 2.0€ to 5.2€
  const parkingPerCapita = 0.5 + parkingLevel * 0.5; // e.g. 1.0€ to 3.0€
  const merchandisingPerCapita = 0.8 + merchandisingLevel * 0.9; // e.g. 1.7€ to 5.3€

  const cafeteriaM = Number(((attendance * cafeteriaPerCapita) / 1_000_000).toFixed(3));
  const parkingM = Number(((attendance * parkingPerCapita) / 1_000_000).toFixed(3));
  const merchandisingM = Number(((attendance * merchandisingPerCapita) / 1_000_000).toFixed(3));

  const commercialRevenueM = Number((cafeteriaM + parkingM + merchandisingM).toFixed(3));

  return {
    ticketRevenueM,
    commercialRevenueM,
    breakdown: {
      cafeteriaM,
      parkingM,
      merchandisingM,
    },
  };
}

/**
 * Aplicar modificadores de staff
 * Hasta +5% por línea (Portería, Físico, Técnico, Tiros)
 * Escala 1-5 estrellas con medias estrellas
 */
export function calculateStaffModifier(staff: ClubStaff): number {
  const avgStars = (staff.goalkeeping + staff.fitness + staff.technical + staff.shooting) / 4;
  // 1 star -> +1%, 5 stars -> +5%
  return Number((avgStars * 0.01).toFixed(4));
}

/**
 * Aplicar modificador de asistencia
 * 50% = 0%, 60% = +1%, 70% = +2%, 80% = +3%, 90% = +4%, 100% = +5%
 * Asistencia < 50% penaliza hasta -5%
 */
export function calculateAttendanceModifier(attendancePercentage: number): number {
  // Linear scale centered at 50%
  const diffFromHalf = attendancePercentage - 50; // -50 to +50
  // Every 10% is 1% modifier
  const modPercent = diffFromHalf * 0.1; // -5.0 to +5.0
  const clamped = Math.max(-5.0, Math.min(5.0, modPercent));
  return Number((clamped / 100).toFixed(4));
}

/**
 * Aplicar dinámica últimos 5 partidos
 * Victoria +1%, empate +0.5%, derrota 0. Máximo +5%.
 */
export function calculateMomentumModifier(recentForm: ('W' | 'D' | 'L')[]): number {
  let momentum = 0;
  recentForm.slice(-5).forEach((res) => {
    if (res === 'W') momentum += 0.01;
    else if (res === 'D') momentum += 0.005;
  });
  return Number(Math.min(0.05, momentum).toFixed(4));
}

/**
 * Match Engine Calculation
 * Deterministic calculation based on line-up players ratings, staff, attendance, and momentum
 */
export function calculateMatchEngine(
  homeClub: Club,
  awayClub: Club,
  homeStarters: Player[],
  awayStarters: Player[]
): MatchEngineCalculation {
  // 1. Raw team points from starters
  const homeAvgOverall = homeStarters.length > 0
    ? homeStarters.reduce((acc, p) => acc + p.overall, 0) / homeStarters.length
    : 60;

  const awayAvgOverall = awayStarters.length > 0
    ? awayStarters.reduce((acc, p) => acc + p.overall, 0) / awayStarters.length
    : 60;

  // Scale raw points around ~20-50 pts base
  const homeRawPoints = homeAvgOverall * 0.45;
  const awayRawPoints = awayAvgOverall * 0.45;

  // 2. Modifiers
  const homeStaffMod = calculateStaffModifier(homeClub.staff);
  const awayStaffMod = calculateStaffModifier(awayClub.staff);

  const { attendance, percentage, generalTicketsSold } = calculateAttendance(
    homeClub.stadium,
    homeClub.recentForm
  );

  const attendanceMod = calculateAttendanceModifier(percentage);

  const homeMomentumMod = calculateMomentumModifier(homeClub.recentForm);
  const awayMomentumMod = calculateMomentumModifier(awayClub.recentForm);

  // 3. Effective points
  // Home gets home advantage: attendanceMod + staffMod + momentumMod
  const homeMultiplier = 1 + homeStaffMod + attendanceMod + homeMomentumMod;
  const awayMultiplier = 1 + awayStaffMod + awayMomentumMod;

  const homeEffectivePoints = homeRawPoints * homeMultiplier;
  const awayEffectivePoints = awayRawPoints * awayMultiplier;

  // 4. Convert points into goals
  // POINTS_PER_GOAL = 10 pts base
  // Goal count is determined deterministically by points difference + baseline
  const POINTS_PER_GOAL = 10;
  const diff = homeEffectivePoints - awayEffectivePoints;

  // Base goals for football realism (average 1 to 2 goals)
  let homeGoals = Math.max(0, Math.floor((homeEffectivePoints - 22) / POINTS_PER_GOAL) + (diff > 4 ? 1 : 0));
  let awayGoals = Math.max(0, Math.floor((awayEffectivePoints - 22) / POINTS_PER_GOAL) + (diff < -4 ? 1 : 0));

  // Small determinism nudge based on club code hash to vary scorelines without randomness breaking reproducibility
  const hashVal = (homeClub.code.charCodeAt(0) + awayClub.code.charCodeAt(0)) % 5;
  if (hashVal === 0 && homeGoals === awayGoals && Math.abs(diff) > 2) {
    if (diff > 0) homeGoals += 1;
    else awayGoals += 1;
  }

  // Cap crazy scores
  homeGoals = Math.max(0, Math.min(6, homeGoals));
  awayGoals = Math.max(0, Math.min(6, awayGoals));

  // Revenues for home team
  const revenues = calculateMatchdayRevenues(
    generalTicketsSold,
    homeClub.stadium.ticketPrice,
    attendance,
    homeClub.stadium
  );

  // Generate simulated goalscorers from starting attackers/midfielders
  const goalScorers: { minute: number; team: 'home' | 'away'; playerName: string }[] = [];
  const homeAttackers = homeStarters.filter((p) => p.position === 'DEL' || p.position === 'MED');
  const awayAttackers = awayStarters.filter((p) => p.position === 'DEL' || p.position === 'MED');

  for (let g = 0; g < homeGoals; g++) {
    const scorer = homeAttackers[g % homeAttackers.length] || homeStarters[0] || { name: 'Goleador Local' };
    goalScorers.push({
      minute: 12 + g * 22 + (g === 1 ? 7 : 0),
      team: 'home',
      playerName: scorer.name,
    });
  }

  for (let g = 0; g < awayGoals; g++) {
    const scorer = awayAttackers[g % awayAttackers.length] || awayStarters[0] || { name: 'Goleador Visitante' };
    goalScorers.push({
      minute: 25 + g * 28 + (g === 1 ? 5 : 0),
      team: 'away',
      playerName: scorer.name,
    });
  }

  goalScorers.sort((a, b) => a.minute - b.minute);

  return {
    homeTeamScore: homeGoals,
    awayTeamScore: awayGoals,
    homeRawPoints: Number(homeRawPoints.toFixed(2)),
    awayRawPoints: Number(awayRawPoints.toFixed(2)),
    modifiers: {
      homeStaffMod: Number((homeStaffMod * 100).toFixed(1)),
      awayStaffMod: Number((awayStaffMod * 100).toFixed(1)),
      attendanceMod: Number((attendanceMod * 100).toFixed(1)),
      homeMomentumMod: Number((homeMomentumMod * 100).toFixed(1)),
      awayMomentumMod: Number((awayMomentumMod * 100).toFixed(1)),
    },
    homeEffectivePoints: Number(homeEffectivePoints.toFixed(2)),
    awayEffectivePoints: Number(awayEffectivePoints.toFixed(2)),
    attendanceCalculated: attendance,
    attendancePercentage: percentage,
    ticketRevenueM: revenues.ticketRevenueM,
    commercialRevenueM: revenues.commercialRevenueM,
    goalScorers,
  };
}

/**
 * Evaluar aceptación del jugador en una oferta de mercado
 * Considera salario, división, duración y prima de fichaje (agentes libres) o cláusula (clubes rivales)
 */
export function evaluatePlayerContractAcceptance(
  player: Player,
  offeredSalaryM: number,
  contractDurationYears: number,
  offeredReleaseClauseM: number,
  offeringClubDivision: string,
  offeredSigningBonusM: number = 0
): {
  accepted: boolean;
  score: number; // 0 to 100
  reason: string;
  feedback: {
    salaryScore: number;
    durationScore: number;
    clauseScore: number;
    divisionScore: number;
    bonusScore?: number;
  };
} {
  const expectedSalary = player.contract.salary;
  const isFreeAgent = player.clubId === null;

  // 1. Salary Score (weight: 50%)
  // Offering 100% of expectation gives 75 pts. 120% gives 100 pts. Below 80% is heavily penalized.
  const salaryRatio = offeredSalaryM / Math.max(0.1, expectedSalary);
  let salaryScore = 0;
  if (salaryRatio >= 1.2) salaryScore = 100;
  else if (salaryRatio >= 1.0) salaryScore = 75 + (salaryRatio - 1.0) * 125;
  else if (salaryRatio >= 0.8) salaryScore = 40 + (salaryRatio - 0.8) * 175;
  else salaryScore = Math.max(0, salaryRatio * 50);

  // 2. Duration Score (weight: 20%)
  // Players > 30 prefer 1-2 years; Players < 26 prefer 3-5 years
  let durationScore = 70;
  if (player.age >= 32) {
    if (contractDurationYears <= 2) durationScore = 95;
    else durationScore = 50;
  } else if (player.age <= 24) {
    if (contractDurationYears >= 3) durationScore = 95;
    else durationScore = 60;
  } else {
    durationScore = contractDurationYears >= 2 && contractDurationYears <= 4 ? 90 : 70;
  }

  // 3. Clause Score vs Signing Bonus Score (weight: 15%)
  // En los agentes libres no habrá cláusula de rescisión sino prima de fichaje
  let clauseScore = 80;
  let bonusScore = 80;

  if (isFreeAgent) {
    const marketVal = player.currentMarketValue || (expectedSalary * 5);
    const expectedBonus = Math.max(0.2, Number((marketVal * 0.08).toFixed(2))); // ~8% del valor de mercado
    if (offeredSigningBonusM >= expectedBonus * 1.5) bonusScore = 100;
    else if (offeredSigningBonusM >= expectedBonus) bonusScore = 90;
    else if (offeredSigningBonusM > 0) bonusScore = 75;
    else bonusScore = 55; // Sin prima de fichaje
    clauseScore = bonusScore;
  } else {
    // Jugador con club: evaluación de cláusula
    const clauseRatio = offeredReleaseClauseM / Math.max(1, player.contract.releaseClause);
    if (clauseRatio < 0.5) clauseScore = 40; // Demasiado baja para su orgullo
    else if (clauseRatio > 4.0) clauseScore = 60; // Demasiado restrictiva
    else clauseScore = 90;
  }

  // 4. Division Score (weight: 15%)
  const divisionScore = offeringClubDivision === 'Primera División' ? 95 : 60;

  // Weighted total score
  const totalScore = Math.round(
    salaryScore * 0.5 + durationScore * 0.2 + (isFreeAgent ? bonusScore : clauseScore) * 0.15 + divisionScore * 0.15
  );

  const accepted = totalScore >= 70;
  let reason = '';
  if (accepted) {
    reason = isFreeAgent
      ? 'El agente libre y su representante consideran muy atractiva la prima de fichaje y la ficha anual, y aceptan firmar el contrato.'
      : 'El jugador y su representante consideran la oferta económica y deportiva muy atractiva y firman con satisfacción.';
  } else if (salaryRatio < 0.9) {
    reason = `Propuesta salarial insuficiente. El jugador espera al menos ${expectedSalary.toFixed(2)} M€ anuales.`;
  } else if (isFreeAgent && bonusScore < 70) {
    reason = 'La prima de fichaje ofrecida es insuficiente para convencer al agente libre de firmar por el club.';
  } else {
    reason = 'Las condiciones de duración o cláusula de rescisión no convencen al jugador para firmar el proyecto deportivo.';
  }

  return {
    accepted,
    score: totalScore,
    reason,
    feedback: {
      salaryScore: Math.round(salaryScore),
      durationScore: Math.round(durationScore),
      clauseScore: Math.round(clauseScore),
      divisionScore: Math.round(divisionScore),
      bonusScore: isFreeAgent ? Math.round(bonusScore) : undefined,
    },
  };
}

/**
 * Stadium expansion costs and capacities
 */
export const STADIUM_TIERS = [
  { capacity: 15000, costM: 0, label: 'Grada Básica Municipal (15.000 asientos)' },
  { capacity: 20000, costM: 12.0, label: 'Ampliación Lateral (20.000 asientos)' },
  { capacity: 30000, costM: 24.0, label: 'Grada Alta Norte y Sur (30.000 asientos)' },
  { capacity: 45000, costM: 38.0, label: 'Remodelación de Cubierta y Segundo Anillo (45.000 asientos)' },
  { capacity: 65000, costM: 55.0, label: 'Estadio de Élite Europea 5 Estrellas (65.000 asientos)' },
];

export const COMMERCIAL_MODULE_COSTS = [
  { level: 1, costM: 0, title: 'Nivel 1 (Básico)' },
  { level: 2, costM: 2.5, title: 'Nivel 2 (Modernizado)' },
  { level: 3, costM: 5.0, title: 'Nivel 3 (Premium)' },
  { level: 4, costM: 8.5, title: 'Nivel 4 (Alta Gama)' },
  { level: 5, costM: 13.0, title: 'Nivel 5 (Élite)' },
];
